/**
 * Auth: passkey sign-in (conditional UI so 1Password / iCloud offer the passkey in the field),
 * invite sign-up (one synced passkey, or two device-bound ones, + recovery codes), recovery-code
 * sign-in, and trusting this device (non-extractable device key endorsed by one passkey tap).
 * A pending `viko setup` / `viko enroll` link (lib/links.ts) continues to "Add this machine".
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import {
  Clock,
  FileKey,
  Fingerprint,
  KeyRound,
  LifeBuoy,
  ShieldCheck,
  Terminal,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Wordmark } from '../components/brand.js'
import { KitInput, RecoveryKitCard } from '../components/RecoveryKit.js'
import { errorText } from '../components/ui.js'
import { ApiError } from '../lib/api.js'
import { useBackend } from '../lib/backend.js'
import {
  deviceLabel,
  endorseDevice,
  endorsed,
  endorsementState,
  loadDevice,
} from '../lib/device-key.js'
import { type FaceIdContent, signInWithFaceId } from '../lib/faceid-signin.js'
import { clearPending, normalizeInvite, readPending } from '../lib/links.js'
import { passkeyErrorMessage, passkeys, supportsLoginJoin } from '../lib/passkeys.js'
import { qk, useSession } from '../lib/queries.js'
import {
  preparePendingKit,
  type RecoveryKit,
  rememberedKit,
  rememberKit,
  restoreWithKit,
} from '../lib/recovery-kit.js'
import {
  abandonSessionOnly,
  isSessionOnly,
  leaveSessionOnlyForTrust,
  startSessionOnly,
} from '../lib/session-only.js'

function AuthFrame({
  title,
  lede,
  children,
}: {
  title: string
  lede?: ReactNode
  children: ReactNode
}) {
  return (
    <main className="auth">
      <div className="auth__card">
        <Wordmark size={24} />
        <div className="grid gap-1.5">
          <h1 className="m-0 text-[26px] leading-8 font-[650] tracking-[-0.02em]">{title}</h1>
          {lede ? <p className="m-0 text-[15px] leading-[22px] text-text-2">{lede}</p> : null}
        </div>
        {children}
      </div>
    </main>
  )
}

function ErrorLine({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <p role="alert" className="m-0 text-[15px] text-status-fail">
      {text}
    </p>
  )
}

/** Where to go once signed in and trusted: a pending machine link, else Now. */
export function nextAfterTrust(): '/enroll' | '/' {
  return readPending()?.e ? '/enroll' : '/'
}

/** After any sign-in: trust the device if needed, then continue (pending machine link or Now). */
function useAfterSignIn() {
  const qc = useQueryClient()
  const nav = useNavigate()
  return async () => {
    await qc.invalidateQueries({ queryKey: qk.session })
    const dev = await loadDevice()
    void nav({ to: endorsed(endorsementState(dev)) ? nextAfterTrust() : '/trust' })
  }
}

/**
 * From the unlock fallback straight to the recovery kit. The session query still holds the
 * signed-out answer from before this sign-in, so refresh it first; otherwise the app guard reads it
 * and bounces back to /signin.
 */
function useOpenRecoveryKit() {
  const qc = useQueryClient()
  const nav = useNavigate()
  return async () => {
    await qc.invalidateQueries({ queryKey: qk.session })
    void nav({ to: '/settings/security' as never, hash: 'recovery-keys' })
  }
}

// ---- sign in --------------------------------------------------------------------------------------

export function SignIn() {
  const b = useBackend()
  const after = useAfterSignIn()
  const openKit = useOpenRecoveryKit()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [recover, setRecover] = useState(false)
  // Sign-in is usernameless (discoverable passkeys): the email field is only an autofill anchor
  // for picking among several accounts, so it stays behind "Use a different account".
  const [other, setOther] = useState(false)
  const started = useRef(false)

  // Phone with one Face ID: when the unlock fallback applies, show it before continuing.
  const [locked, setLocked] = useState<FaceIdContent | null>(null)
  const oneTap = typeof passkeys.loginJoin === 'function' && supportsLoginJoin()
  const signInOnce = async (conditional: boolean, session = false) => {
    if (oneTap) {
      // One ceremony: sign in + trust this browser (or just this session) + (with PRF) unlock.
      const r = await signInWithFaceId({
        api: b.api,
        passkeys,
        ...(b.content ? { content: b.content } : {}),
        ...(conditional ? { conditional } : {}),
        ...(session ? { session } : {}),
      })
      // unlock-via-mac: the same tap asked a key-holding machine; the app shows its progress.
      if (r.machine) {
        void b.lock.track(r.machine).catch(() => {})
        return true
      }
      if (r.content === 'no-prf' || r.content === 'no-escrow') {
        setLocked(r.content)
        return false
      }
      return true
    }
    const options = await b.api.loginOptions()
    const response = await passkeys.login(options, conditional ? { conditional } : undefined)
    setBusy(true)
    await b.api.loginVerify(response)
    return true
  }

  // Conditional mediation: the browser offers passkeys in the username field's autofill.
  // biome-ignore lint/correctness/useExhaustiveDependencies: signInOnce closes over b/after
  useEffect(() => {
    if (!other || started.current) return
    started.current = true
    let alive = true
    ;(async () => {
      if (!(await passkeys.conditionalAvailable().catch(() => false))) return
      try {
        const done = await signInOnce(true)
        if (!alive) return
        if (done) await after()
      } catch (e) {
        const name = (e as { name?: string }).name
        if (alive && name !== 'AbortError') setError(signInError(e))
      } finally {
        if (alive) setBusy(false)
      }
    })()
    return () => {
      alive = false
      started.current = false
      passkeys.cancel()
    }
  }, [b, after, other])

  const explicit = async (session = false) => {
    setError(null)
    setBusy(true)
    passkeys.cancel()
    try {
      if (await signInOnce(false, session)) await after()
    } catch (e) {
      setError(signInError(e))
    } finally {
      setBusy(false)
    }
  }

  if (recover) return <RecoverySignIn onBack={() => setRecover(false)} />
  if (locked)
    return (
      <UnlockFallback
        reason={locked}
        onContinue={() => void after()}
        onRecover={() => void openKit()}
      />
    )

  return (
    <AuthFrame
      title="Sign in to Viko"
      lede="Use Face ID, Touch ID, 1Password or your security key. No email needed."
    >
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          void explicit()
        }}
      >
        {other ? (
          <label className="field">
            <span>Email username (optional)</span>
            <input
              className="input"
              name="username"
              type="email"
              autoComplete="username webauthn"
              placeholder="you@example.com"
              aria-describedby="signin-help"
            />
          </label>
        ) : null}
        <Button
          variant="primary"
          className="vk-btn--block !min-h-11"
          icon={<Fingerprint size={17} />}
          disabled={busy}
          type="submit"
        >
          {busy ? 'Checking passkey…' : 'Sign in with a passkey'}
        </Button>
        {oneTap ? (
          <Button
            className="vk-btn--block !min-h-11"
            icon={<Clock size={16} />}
            disabled={busy}
            type="button"
            aria-describedby="signin-session-help"
            onClick={() => void explicit(true)}
          >
            Just this session
          </Button>
        ) : null}
        {oneTap ? (
          <p id="signin-session-help" className="m-0 text-[14px] text-text-3">
            On a shared or borrowed computer: nothing stays in this browser, and it signs out when
            you close the tab or after 12 hours.
          </p>
        ) : null}
        <ErrorLine text={error} />
      </form>
      <p id="signin-help" className="m-0 grid gap-2 text-[14px] text-text-3">
        {other ? null : (
          <button
            type="button"
            className="w-fit cursor-pointer border-0 bg-transparent p-0 text-left text-accent-text"
            onClick={() => setOther(true)}
          >
            Use a different account
          </button>
        )}
        <span>
          New to Viko?{' '}
          <Link to="/setup" className="text-accent-text">
            Create an account with your invite
          </Link>
        </span>
        <button
          type="button"
          className="w-fit cursor-pointer border-0 bg-transparent p-0 text-left text-accent-text"
          onClick={() => setRecover(true)}
        >
          Lost your passkeys? Use your recovery kit
        </button>
      </p>
    </AuthFrame>
  )
}

function signInError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'unknown_credential') return 'That passkey is not registered with Viko.'
    if (e.code === 'setup_incomplete')
      return 'Account setup is not finished. Open your invite link again to finish it.'
    if (e.code === 'rate_limited') return 'Too many attempts. Wait a few minutes and try again.'
    return e.message
  }
  return passkeyErrorMessage(e)
}

/**
 * Signed in and trusted, but this passkey can't unlock encrypted work in the same tap. Nothing
 * falls back to plaintext: the kit (24 h hold unless a trusted device approves) or a device that
 * already reads the work (which can turn on Face ID unlock for this passkey) are the ways in.
 */
export function UnlockFallback({
  reason,
  onContinue,
  onRecover,
}: {
  reason: FaceIdContent
  onContinue: () => void
  /** Opens the recovery kit (refreshing the session first so the app guard sees the sign-in). */
  onRecover: () => void
}) {
  return (
    <AuthFrame
      title="Unlock your encrypted work on this phone"
      lede={`You’re signed in${isSessionOnly() ? ' for this session' : ' and this device is trusted'}. ${
        reason === 'no-prf'
          ? 'This passkey can’t also unlock encrypted work (it doesn’t support the PRF secret Viko needs), so your work stays locked here for now.'
          : 'This passkey hasn’t been set up to unlock encrypted work yet, so your work stays locked here for now.'
      }`}
    >
      <ul className="m-0 grid gap-2 pl-5 text-[15px] text-text-2">
        <li>
          <strong className="font-semibold">Use your recovery kit.</strong> Another signed-in device
          can approve it right away; otherwise your work unlocks after 24 hours.
        </li>
        <li>
          <strong className="font-semibold">Or turn on Face ID unlock</strong> from a device that
          already shows your work: You › Security › Protect with passkey. Next time, one Face ID
          does everything.
        </li>
      </ul>
      <Button
        variant="primary"
        className="vk-btn--block !min-h-11"
        icon={<FileKey size={16} />}
        onClick={onRecover}
      >
        Use your recovery kit
      </Button>
      <Button className="vk-btn--block !min-h-11" onClick={onContinue}>
        Continue without encrypted work
      </Button>
    </AuthFrame>
  )
}

function RecoverySignIn({ onBack }: { onBack: () => void }) {
  const b = useBackend()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [userId, setUserId] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const signInWithKit = async (kit: Omit<RecoveryKit, 'createdAt'>) => {
    setError(null)
    setBusy(true)
    try {
      if (!kit.codes.length) throw new ApiError(401, 'invalid_code')
      // Every code in the kit, one attempt: the hub consumes the first unused one.
      await b.api.recover(kit.accountId, kit.codes)
      rememberKit(kit)
      await qc.invalidateQueries({ queryKey: qk.session })
      void nav({ to: '/setup' })
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? 'This kit’s sign-in codes are used up or were replaced by a newer kit.'
          : errorText(err),
      )
    } finally {
      setBusy(false)
    }
  }
  return (
    <AuthFrame
      title="Recover with your kit"
      lede="Your recovery kit signs you in to create a new passkey, then unlocks your encrypted work. Your machines keep trusting only passkeys you approve on them."
    >
      <KitInput onKit={signInWithKit} busy={busy} submitLabel="Sign in with this kit">
        <ErrorLine text={error} />
      </KitInput>
      <details className="text-[14px]">
        <summary className="cursor-pointer text-accent-text">Enter one code by hand</summary>
        <form
          className="grid gap-3"
          onSubmit={async (e) => {
            e.preventDefault()
            setError(null)
            try {
              await b.api.recover(userId.trim(), code.trim())
              await qc.invalidateQueries({ queryKey: qk.session })
              void nav({ to: '/setup' })
            } catch (err) {
              setError(
                err instanceof ApiError && err.code === 'invalid_code'
                  ? 'That code is not valid or was already used.'
                  : errorText(err),
              )
            }
          }}
        >
          <label className="field">
            <span>Account id</span>
            <input
              className="input font-mono"
              value={userId}
              onChange={(e) => setUserId(e.target.value.trim())}
              autoComplete="username"
              aria-describedby="recover-help"
              required
            />
          </label>
          <p id="recover-help" className="m-0 text-[14px] text-text-3">
            Your account id is printed at the top of your recovery kit.
          </p>
          <label className="field">
            <span>Recovery code</span>
            <input
              className="input font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              required
            />
          </label>
          <Button variant="primary" type="submit" icon={<LifeBuoy size={16} />}>
            Use recovery code
          </Button>
        </form>
      </details>
      <button
        type="button"
        className="w-fit cursor-pointer border-0 bg-transparent p-0 text-[14px] text-accent-text"
        onClick={onBack}
      >
        Back to passkey sign-in
      </button>
    </AuthFrame>
  )
}

// ---- sign-up ----------------------------------------------------------------------------------------

function signupError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'invalid_invite')
      return 'That invite isn’t valid. It may have been used already or expired — ask for a new one.'
    if (e.code === 'rate_limited') return 'Too many attempts. Wait a few minutes and try again.'
    if (e.code === 'unauthenticated')
      return 'This sign-up session ended. Open your invite link again to start over.'
    return e.message
  }
  return passkeyErrorMessage(e)
}

/**
 * Sign-up step 3: the browser creates the recovery kit. The data recovery key is generated here
 * and only its escrow (ciphertext) is kept until the first machine's approval creates the genesis
 * directory; with a PRF result from registration, the passkey's v2 PRF escrow is prepared too.
 */
function KitStep({
  codes,
  accountId,
  email,
  prf,
  onDone,
}: {
  codes: string[]
  accountId: string
  email?: string
  prf: { current: { credentialId: string; output: Uint8Array } | undefined }
  onDone: () => void
}) {
  const [kit, setKit] = useState<RecoveryKit | null>(null)
  const started = useRef<Promise<string | undefined> | null>(null)
  useEffect(() => {
    started.current ??= preparePendingKit(accountId, prf.current)
      .catch(() => undefined)
      .finally(() => {
        prf.current?.output.fill(0)
        prf.current = undefined
      })
    let alive = true
    void started.current.then((dataKey) => {
      if (alive)
        setKit({
          accountId,
          codes,
          ...(email ? { email } : {}),
          ...(dataKey ? { dataKey } : {}),
          createdAt: Date.now(),
        })
    })
    return () => {
      alive = false
    }
  }, [accountId, codes, email, prf])
  return (
    <AuthFrame
      title="Save your recovery kit"
      lede="One file with your account id, sign-in recovery codes and a data recovery key. If you ever lose every device and passkey, it gets you and your encrypted work back."
    >
      {kit ? (
        <RecoveryKitCard kit={kit} onDone={onDone} />
      ) : (
        <p role="status">Preparing your kit…</p>
      )}
    </AuthFrame>
  )
}

/**
 * Signed in with the recovery kit (recovery scope): create a new passkey, trust this device, then
 * start unlocking encrypted work with the kit's data recovery key (held 24 h unless approved).
 */
function RecoveredAccess() {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [held, setHeld] = useState<number | null>(null)
  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      const options = await b.api.credentialOptions('viko')
      const response = await passkeys.register(options)
      await b.api.credentialVerify(response, deviceLabel())
      await qc.invalidateQueries({ queryKey: qk.session })
      const endorse = () => endorseDevice(passkeys, (e) => b.api.registerDevice(e))
      const dev = await endorse()
      b.deviceKeyId = dev.deviceKeyId
      const kit = rememberedKit()
      rememberKit(undefined)
      if (kit?.dataKey && b.content) {
        const r = await restoreWithKit(b.content, b.api, kit.dataKey, { endorse })
        if (r.status === 'held') {
          setHeld(r.request.availableAt)
          return
        }
      }
      void nav({ to: '/' })
    } catch (e) {
      setError(signupError(e))
    } finally {
      setBusy(false)
    }
  }
  if (held !== null)
    return (
      <AuthFrame
        title="Your work unlocks soon"
        lede={`For your safety, encrypted work is released ${new Date(held).toLocaleString()} unless another signed-in device approves sooner. Every signed-in device was alerted.`}
      >
        <p className="m-0 text-[15px] text-text-2">
          Then open You › Security on this device and open your kit again to finish. On each
          machine, run <code className="font-mono">viko passkeys re-pin</code> to trust your new
          passkey.
        </p>
        <Button
          variant="primary"
          className="vk-btn--block !min-h-11"
          onClick={() => void nav({ to: '/' })}
        >
          Continue
        </Button>
      </AuthFrame>
    )
  return (
    <AuthFrame
      title="Create a new passkey"
      lede="Your kit signed you in. Create a passkey on this device to finish; one more tap trusts this device."
    >
      <Button
        variant="primary"
        className="vk-btn--block !min-h-11"
        disabled={busy}
        icon={<KeyRound size={16} />}
        onClick={() => void create()}
      >
        {busy ? 'Waiting for passkey…' : 'Create passkey'}
      </Button>
      <ErrorLine text={error} />
    </AuthFrame>
  )
}

/**
 * `/setup`: invite → one passkey (synced passkeys are enough; a security key needs a backup) →
 * recovery codes → trust this device → add the machine that opened the link. Signed-in visitors
 * with a machine link go straight to "Add this machine".
 */
export function Setup() {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const session = useSession()
  const pending = readPending()
  const [invite, setInvite] = useState(pending?.i ?? '')
  const [stage, setStage] = useState<'invite' | 'passkey' | 'backup' | 'codes'>('invite')
  const [codes, setCodes] = useState<string[] | null>(null)
  // PRF output from the registration ceremony, held only until the kit step prepares its escrow.
  const prf = useRef<{ credentialId: string; output: Uint8Array } | undefined>(undefined)
  const [accountId, setAccountId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const autoStarted = useRef(false)
  const s = session.data
  const scope = s?.authenticated ? s.scope : undefined

  // Already signed in with a machine link: skip sign-up.
  useEffect(() => {
    if (scope === 'full' && s?.setupState === 'complete' && s?.email && !codes) {
      void nav({ to: pending?.e ? '/enroll' : '/', replace: true })
    }
  }, [scope, s?.setupState, s?.email, codes, nav, pending?.e])

  // Resuming a sign-up session (page reload between passkeys).
  useEffect(() => {
    if (scope === 'setup' && stage === 'invite') {
      setAccountId(s?.userId ?? '')
      setStage((s?.credentials ?? 0) > 0 ? 'backup' : 'passkey')
    }
  }, [scope, stage, s?.userId, s?.credentials])

  const redeem = async (code: string) => {
    setBusy(true)
    setError(null)
    try {
      const n = normalizeInvite(code)
      if (!n) throw new ApiError(403, 'invalid_invite')
      const r = await b.api.signup(n)
      clearPending('i')
      setAccountId(r.accountId)
      await qc.invalidateQueries({ queryKey: qk.session })
      setStage('passkey')
    } catch (e) {
      setError(signupError(e))
    } finally {
      setBusy(false)
    }
  }

  // An invite link: redeem without asking.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once for a linked invite
  useEffect(() => {
    if (autoStarted.current || stage !== 'invite' || !pending?.i) return
    if (session.isPending || scope) return
    autoStarted.current = true
    void redeem(pending.i)
  }, [session.isPending, scope, stage])

  const register = async () => {
    setBusy(true)
    setError(null)
    try {
      const options = await b.api.setupOptions('Viko')
      const registered = passkeys.registerWithPrf
        ? await passkeys.registerWithPrf(options)
        : { response: await passkeys.register(options) }
      const response = registered.response
      if (registered.prf) {
        prf.current?.output.fill(0)
        prf.current = { credentialId: response.id, output: registered.prf }
      }
      const r = await b.api.setupVerify(
        response,
        stage === 'backup' ? 'Backup passkey' : deviceLabel(),
      )
      await qc.invalidateQueries({ queryKey: qk.session })
      if (r.recoveryCodes) {
        setCodes(r.recoveryCodes)
        setStage('codes')
      } else setStage('backup')
    } catch (e) {
      setError(signupError(e))
    } finally {
      setBusy(false)
    }
  }

  if (stage === 'codes' && codes)
    return (
      <KitStep
        codes={codes}
        accountId={accountId || s?.userId || ''}
        {...(s?.email ? { email: s.email } : {})}
        prf={prf}
        onDone={() => {
          // The account's first browser must be trusted: it approves the first machine.
          firstDevice = true
          void nav({ to: '/trust' })
        }}
      />
    )

  if (scope === 'recovery') return <RecoveredAccess />

  if (s?.authenticated && !s.email)
    return (
      <AuthFrame
        title="Add your email username"
        lede="Keep your existing account and passkeys. Add the email address you want to use as your username."
      >
        <EmailCompletion />
      </AuthFrame>
    )

  if (stage === 'passkey' || stage === 'backup')
    return (
      <AuthFrame
        title={stage === 'passkey' ? 'Create your passkey' : 'Add a backup passkey'}
        lede={
          stage === 'passkey'
            ? 'Your passkey signs you in and approves your machines. Use Touch ID, Windows Hello, your phone, or a password manager like 1Password.'
            : 'That passkey lives only on one device or security key. Add a second one — another key, your phone, or a password manager — so losing it never locks you out.'
        }
      >
        <p className="m-0 text-[15px] text-text-2">Email username: {s?.email}</p>
        <Button
          variant="primary"
          className="vk-btn--block !min-h-11"
          disabled={busy}
          icon={<KeyRound size={16} />}
          onClick={() => void register()}
        >
          {busy
            ? 'Waiting for passkey…'
            : stage === 'passkey'
              ? 'Create passkey'
              : 'Add backup passkey'}
        </Button>
        <ErrorLine text={error} />
      </AuthFrame>
    )

  return (
    <AuthFrame
      title="Create your Viko account"
      lede={
        pending?.e
          ? 'Your computer opened this page to connect to Viko. Enter your invite code to create your account, or sign in if you already have one.'
          : 'Viko is invite-only. Enter the invite code from your invite link.'
      }
    >
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          void redeem(invite)
        }}
      >
        <label className="field">
          <span>Invite code</span>
          <input
            className="input font-mono uppercase"
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            placeholder="26 letters and digits"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            required
          />
        </label>
        <Button
          variant="primary"
          className="vk-btn--block !min-h-11"
          type="submit"
          disabled={busy || invite.trim().length < 20}
        >
          {busy ? 'Checking invite…' : 'Continue'}
        </Button>
        <ErrorLine text={error} />
      </form>
      <div className="grid gap-2 text-[14px] text-text-3">
        <Link to="/signin" className="text-accent-text">
          Already have an account? Sign in
        </Link>
        {pending?.e ? null : (
          <p className="m-0 flex gap-2">
            <Terminal size={15} className="mt-0.5 flex-none" aria-hidden="true" />
            <span>
              Setting up a computer? Run <code className="font-mono">viko setup</code> in its
              terminal — it opens this page for you.
            </span>
          </p>
        )}
      </div>
    </AuthFrame>
  )
}

/** Completes only the authenticated account; never creates another account or verifies ownership. */
export function EmailCompletion() {
  const b = useBackend()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <form
      className="grid gap-3"
      onSubmit={async (event) => {
        event.preventDefault()
        setBusy(true)
        setError(null)
        try {
          await b.api.completeEmail(email.trim())
          await qc.invalidateQueries({ queryKey: qk.session })
        } catch (err) {
          setError(errorText(err))
        } finally {
          setBusy(false)
        }
      }}
    >
      <label className="field">
        <span>Email username</span>
        <input
          className="input"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <p className="m-0 text-[14px] text-text-3">
        This is a username. Viko does not verify mailbox ownership or send email.
      </p>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Save email username'}
      </Button>
      <ErrorLine text={error} />
    </form>
  )
}

// ---- trust this device ------------------------------------------------------------------------------

/** Set when sign-up continues to /trust: the first browser can't be session-only. */
let firstDevice = false

export function TrustDevice() {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const [label, setLabel] = useState(() => deviceLabel())
  const [busy, setBusy] = useState<'trust' | 'session' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Adding a machine (a pending `viko setup` link) or the account's first browser needs trust.
  const sessionAllowed = !firstDevice && !readPending()?.e
  const trust = async (session: boolean) => {
    setBusy(session ? 'session' : 'trust')
    setError(null)
    if (session) startSessionOnly()
    else leaveSessionOnlyForTrust()
    try {
      const dev = await endorseDevice(passkeys, (en) => b.api.registerDevice(en), {
        label,
        ...(session ? { session } : {}),
      })
      b.deviceKeyId = dev.deviceKeyId
      if (session) b.armSessionOnly(dev.deviceKeyId)
      firstDevice = false
      await qc.invalidateQueries({ queryKey: ['device'] })
      await qc.invalidateQueries({ queryKey: qk.devices })
      void nav({ to: nextAfterTrust() })
    } catch (err) {
      if (session) abandonSessionOnly()
      setError(err instanceof ApiError ? err.message : passkeyErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }
  return (
    <AuthFrame
      title="Trust this device"
      lede={
        sessionAllowed
          ? 'Trust this browser: Viko keeps a signing key here that never leaves it, and your machines accept commands from it for 90 days. On a shared computer, choose Just this session: the key lives only in this tab and ends when you close it (12 hours at most).'
          : 'Viko creates a signing key that never leaves this browser. One passkey tap lets your machines accept commands from it for 90 days.'
      }
    >
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          void trust(false)
        }}
      >
        <label className="field">
          <span>Device name</span>
          <input
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={60}
          />
        </label>
        <Button
          variant="primary"
          className="vk-btn--block !min-h-11"
          type="submit"
          disabled={!!busy}
          icon={<ShieldCheck size={16} />}
        >
          {busy === 'trust' ? 'Waiting for passkey…' : 'Trust this device'}
        </Button>
        {sessionAllowed ? (
          <Button
            className="vk-btn--block !min-h-11"
            type="button"
            disabled={!!busy}
            icon={<Clock size={16} />}
            onClick={() => void trust(true)}
          >
            {busy === 'session' ? 'Waiting for passkey…' : 'Just this session'}
          </Button>
        ) : null}
        <ErrorLine text={error} />
      </form>
      <p className="m-0 text-[14px] text-text-3">
        You can revoke this device any time from You › Devices.
      </p>
    </AuthFrame>
  )
}
